import classNames from 'classnames'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { IOrganization } from '@/models'
import { useQueryGetOrganizations } from '@/services/organization.service'

/**
 * Picks one epilot organization by name or id (same combobox as the parent-organization
 * picker in the organization module). The value is the organization id.
 */
export const OrganizationPicker = ({
  value,
  onChange,
  disabled,
  id,
  filter
}: {
  value: string
  onChange: (orgId: string) => void
  disabled?: boolean
  id?: string
  /** Only offer organizations this returns true for. */
  filter?: (organization: IOrganization) => boolean
}) => {
  const [open, setOpen] = useState(false)
  const { data: allOrganizations, isLoading } = useQueryGetOrganizations()
  const organizations = filter
    ? allOrganizations?.filter(filter)
    : allOrganizations
  const selected = organizations?.find((org) => org.id === value)

  // modal: the picker opens inside the enablement dialog, so its layer must own pointer events
  return (
    <Popover modal onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          className={classNames('w-full justify-between font-normal', {
            'text-primary': value,
            'text-placeholder': !value
          })}
          disabled={disabled}
          id={id}
          role="combobox"
          type="button"
          variant="outline"
        >
          <span className="truncate">
            {value
              ? selected
                ? `${selected.name ?? ''} · ${value}`
                : value
              : 'Search by organization name or ID'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0"
      >
        <Command>
          <CommandInput className="h-9" placeholder="Search organizations..." />
          <CommandList>
            <CommandEmpty>
              {isLoading ? 'Loading organizations…' : 'No organization found.'}
            </CommandEmpty>
            <CommandGroup>
              {organizations?.map((org) => (
                <CommandItem
                  className="flex items-center justify-between py-2 px-4"
                  key={org.id}
                  onSelect={() => {
                    onChange(org.id!)
                    setOpen(false)
                  }}
                  value={`${org.name ?? ''} ${org.id ?? ''}`}
                >
                  <div className="flex items-center gap-2">
                    <Check
                      className={classNames(
                        'mr-2 h-4 w-4',
                        value === org.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <div className="flex flex-col">
                      <span className="font-medium">{org.name ?? ''}</span>
                      <span className="text-xs text-muted-foreground">
                        ID: {org.id ?? ''}
                      </span>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
